import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
  AI_CLOSE_TIMEOUT_MS,
  AI_IDLE_TIMEOUT_MS,
} from '../core/constants/ai.const';
import { responseStatus } from '../core/constants/responseConst';
import { AiConvoIdleMessage } from '../models/ai.model';
import { aiService } from './ai.service';
import { LoggerService } from './logger.service';

export interface AiChatIdleHandlers {
  /** Show the server's (already localised) follow-up as a normal AI bubble. */
  onFollowUp: (message: AiConvoIdleMessage) => void;
  /** The chat is closed on the server — clear it locally. */
  onClosed: () => void;
}

type IdlePhase = 'off' | 'idle' | 'followUp' | 'ending';

/**
 * Idle follow-up + auto-close for one AI chat window. Provide it on the chat
 * component so its timers die with the window.
 *
 * start() after each AI reply → 30s → `/ai/convo/idle` (follow-up)
 * → 60s → `/ai/convo/end`. reset() on user activity, stop() for agent chats.
 *
 * Timer callbacks only re-evaluate timestamps, so the same path also handles
 * returning from the background (where mobile/tab timers are unreliable).
 */
@Injectable()
export class AiChatIdleTimerService {
  private readonly ai = inject(aiService);
  private readonly logger = inject(LoggerService);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private handlers: AiChatIdleHandlers | null = null;
  private phase: IdlePhase = 'off';
  private chatId = '';
  /** Last user activity, i.e. when the latest AI reply was shown. */
  private idleSinceAt = 0;
  private followUpShownAt = 0;
  /** Guards against a second `/idle` call in the same idle period. */
  private idleRequested = false;
  /** Chat that was ended, closed or handed to an agent — never tracked again. */
  private retiredChatId = '';
  /** Bumped on every phase change so late API responses are ignored. */
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;

  private readonly onVisibilityChange = (): void => {
    if (this.document.visibilityState === 'hidden') {
      this.clearTimer();
    } else {
      this.evaluate();
    }
  };

  constructor() {
    if (!this.isBrowser) {
      return;
    }
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    inject(DestroyRef).onDestroy(() => {
      this.document.removeEventListener(
        'visibilitychange',
        this.onVisibilityChange
      );
      this.stop();
    });
  }

  configure(handlers: AiChatIdleHandlers): void {
    this.handlers = handlers;
  }

  /** Call once an AI reply is fully shown. */
  start(chatId: string): void {
    if (!this.isBrowser || !chatId || chatId === this.retiredChatId) {
      return;
    }
    this.enterPhase('idle');
    this.chatId = chatId;
    this.idleSinceAt = Date.now();
    this.idleRequested = false;
    this.evaluate();
  }

  /** User activity: cancel everything until the next AI reply. */
  reset(): void {
    // `/end` is already closing the chat.
    if (this.phase === 'ending') {
      return;
    }
    this.enterPhase('off');
  }

  stop(): void {
    this.enterPhase('off');
  }

  private evaluate(): void {
    this.clearTimer();
    if (this.document.visibilityState === 'hidden') {
      return;
    }

    const now = Date.now();
    if (this.phase === 'idle') {
      const idleFor = now - this.idleSinceAt;
      if (idleFor >= AI_IDLE_TIMEOUT_MS + AI_CLOSE_TIMEOUT_MS) {
        this.end();
      } else if (idleFor >= AI_IDLE_TIMEOUT_MS) {
        this.requestFollowUp();
      } else {
        this.schedule(AI_IDLE_TIMEOUT_MS - idleFor);
      }
    } else if (this.phase === 'followUp') {
      const waited = now - this.followUpShownAt;
      if (waited >= AI_CLOSE_TIMEOUT_MS) {
        this.end();
      } else {
        this.schedule(AI_CLOSE_TIMEOUT_MS - waited);
      }
    }
  }

  private requestFollowUp(): void {
    if (this.idleRequested) {
      return;
    }
    this.idleRequested = true;
    const generation = this.generation;
    const chatId = this.chatId;

    this.ai.aiConvoIdle(chatId).subscribe({
      next: res => {
        if (generation !== this.generation) {
          return;
        }
        if (res.statusCode === responseStatus.BADREQUEST) {
          // Already closed/resolved on the server — same as a successful /end.
          this.finishClosed();
          return;
        }
        if (res.statusCode !== responseStatus.SUCCESS) {
          this.enterPhase('off');
          return;
        }

        const message = res.data?.message ?? null;
        if (!message) {
          // A human agent owns the chat.
          this.retiredChatId = chatId;
          this.enterPhase('off');
          return;
        }

        this.enterPhase('followUp');
        this.followUpShownAt = Date.now();
        this.handlers?.onFollowUp(message);
        this.evaluate();
      },
      error: (error: unknown) => {
        if (generation !== this.generation) {
          return;
        }
        if (
          error instanceof HttpErrorResponse &&
          error.status === responseStatus.BADREQUEST
        ) {
          this.finishClosed();
          return;
        }
        this.logger.error('AI idle follow-up request failed', error);
        this.enterPhase('off');
      },
    });
  }

  private end(): void {
    const chatId = this.chatId;
    if (chatId === this.retiredChatId) {
      return;
    }
    this.retiredChatId = chatId;
    this.enterPhase('ending');
    const generation = this.generation;

    this.ai.aiConvoEnd(chatId).subscribe({
      next: res => {
        if (generation !== this.generation) {
          return;
        }
        if (
          res.statusCode === responseStatus.SUCCESS &&
          res.data?.closed === false
        ) {
          // A human agent owns the chat — keep it as it is.
          this.enterPhase('off');
          return;
        }
        this.finishClosed();
      },
      error: (error: unknown) => {
        if (generation !== this.generation) {
          return;
        }
        // Clear locally anyway; the next `/ai/convo/start` recovers.
        this.logger.error('AI chat end request failed', error);
        this.finishClosed();
      },
    });
  }

  private finishClosed(): void {
    this.retiredChatId = this.chatId;
    this.enterPhase('off');
    this.handlers?.onClosed();
  }

  private enterPhase(phase: IdlePhase): void {
    this.clearTimer();
    this.generation += 1;
    this.phase = phase;
  }

  private schedule(delayMs: number): void {
    this.timer = setTimeout(() => {
      this.timer = null;
      this.evaluate();
    }, delayMs);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
